import { Link } from "react-router-dom";
import {
  CloudSun, Plant, Drop, Bug, Camera, GraduationCap, Calendar, ChartLineUp,
  Basket, Warning, Sparkle, PlusCircle,
} from "@phosphor-icons/react";
import { IS_FARMERSPULSE } from "@/lib/site";

/**
 * WhatDoYouNeed — the "doing not reading" hero grid.
 *
 * Twelve big-button tiles that answer the reviewer's core prompt
 * "What would you like help with today?". Every tile is a single-tap
 * link to the FarmersPulse surface that actually does the thing.
 *
 * Rendered near the top of FarmersPulseHome so first-time visitors
 * see an action-oriented product, not a reading experience.
 */
const NAV_BASE = IS_FARMERSPULSE ? "" : "/farmers";

// Each tile now routes to its evergreen article at /tools/<slug>.
// The article page then offers a hard CTA into the actual FarmersPulse
// surface that does the work. Slugs mirror the entries in
// `src/data/toolArticles.js`.
const TILES = [
  { key: "weather",   emoji: "🌦", icon: CloudSun,     label: "My Weather",           slug: "my-weather" },
  { key: "plant",     emoji: "🌱", icon: Plant,        label: "What Should I Plant?", slug: "what-should-i-plant" },
  { key: "irrigate",  emoji: "💧", icon: Drop,         label: "Should I Irrigate?",   slug: "should-i-irrigate" },
  { key: "pest",      emoji: "🐛", icon: Bug,          label: "Identify a Pest",      slug: "identify-a-pest" },
  { key: "photo",     emoji: "📷", icon: Camera,       label: "Analyze Plant Photo",  slug: "analyze-plant-photo" },
  { key: "mycrops",   emoji: "🌾", icon: GraduationCap, label: "My Crops",             slug: "my-crops" },
  { key: "calendar",  emoji: "📅", icon: Calendar,     label: "Farm Calendar",        slug: "farm-calendar" },
  { key: "markets",   emoji: "📈", icon: ChartLineUp,  label: "Market Prices",        slug: "market-prices" },
  { key: "sell",      emoji: "🛒", icon: Basket,       label: "Sell My Produce",      slug: "sell-my-produce" },
  { key: "alerts",    emoji: "⚠️", icon: Warning,      label: "Nearby Farm Alerts",   slug: "nearby-farm-alerts" },
  { key: "ask",       emoji: "🤖", icon: Sparkle,      label: "Ask FarmersPulse AI",  slug: "ask-farmerspulse-ai" },
  { key: "report",    emoji: "📝", icon: PlusCircle,   label: "Report What I See",    slug: "report-a-farm-observation" },
];

const ACADEMY_HREF = `${NAV_BASE}/academy`;
const CLIMATE_HREF = `${NAV_BASE}/climate`;

export default function WhatDoYouNeed() {
  return (
    <section
      className="max-w-[1400px] mx-auto px-5 md:px-10 mt-10"
      data-testid="farmers-what-do-you-need"
    >
      <div className="rounded-3xl border border-[#0F3D2A]/10 bg-white/70 backdrop-blur px-6 md:px-10 py-8 md:py-10">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#2D8659]">
              Farmers Pulse · Right now
            </div>
            <h2 className="mt-1 font-serif-display text-3xl md:text-4xl leading-tight text-[#151312]">
              What would you like help with today?
            </h2>
          </div>
          <p className="text-sm text-[#3A3830] max-w-md">
            One tap gets you to the tool. FarmersPulse is a daily companion — not another article to read.
          </p>
        </div>

        <Link
          to={ACADEMY_HREF}
          data-testid="wdyn-academy-cta"
          className="mt-4 group flex flex-wrap items-center gap-3 rounded-2xl bg-[#0F3D2A] text-[#F5EBDC] p-4 md:p-5 hover:bg-[#124f38] transition"
        >
          <span aria-hidden className="text-2xl">🎓</span>
          <div className="flex-1 min-w-0">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#F9C784]">
              Farm Growth Academy · New
            </div>
            <div className="font-serif-display text-lg md:text-xl leading-tight">
              Learn it. Build it. Launch it. Measure it.
            </div>
          </div>
          <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#F9C784] group-hover:text-white">
            Open Academy →
          </span>
        </Link>

        <Link
          to={CLIMATE_HREF}
          data-testid="wdyn-climate-cta"
          className="mt-3 group flex flex-wrap items-center gap-3 rounded-2xl bg-[#0F3D2A]/90 text-[#F5EBDC] p-4 md:p-5 hover:bg-[#124f38] transition border border-[#2D8659]/40"
        >
          <span aria-hidden className="text-2xl">🌱</span>
          <div className="flex-1 min-w-0">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#F9C784]">
              Climate-Smart Farming · New
            </div>
            <div className="font-serif-display text-lg md:text-xl leading-tight">
              Adopt, document, and benefit — without credit promises.
            </div>
          </div>
          <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#F9C784] group-hover:text-white">
            Open Climate →
          </span>
        </Link>

        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="what-do-you-need-grid">
          {TILES.map(({ key, emoji, icon: Icon, label, slug }) => (
            <Link
              key={key}
              to={`${NAV_BASE}/tools/${slug}`}
              data-testid={`wdyn-tile-${key}`}
              className="group rounded-2xl border border-[#0F3D2A]/10 bg-white hover:border-[#2D8659] hover:shadow-[0_10px_30px_-20px_rgba(15,61,42,0.35)] transition-all p-4 flex flex-col items-start gap-2 text-left"
            >
              <div className="flex items-center gap-2 text-[#2D8659]">
                <span aria-hidden className="text-2xl leading-none">{emoji}</span>
                <Icon size={16} weight="fill" className="opacity-70" />
              </div>
              <div className="font-serif-display text-base md:text-lg leading-tight text-[#151312] group-hover:text-[#2D8659]">
                {label}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
